import { mergePopularidadEncabezado } from './popularidadReportModel';
import { isoADisplayDDMMAAAA } from './fechaReporte';

export const GASTOS_REPORT_TITULO_DEFAULT = 'REPORTE DE GASTOS';

/** Catálogo base SmartNet (tipos 1–6) cuando la fila no trae `tipoNombre`. */
export const TIPO_GASTOS_LEGACY = {
  1: 'COMPRA DE ALIMENTOS',
  2: 'COMPRA DE BEBIDAS',
  3: 'PAGO DE SERVICIO',
  4: 'PAGO A PROVEEDORES',
  5: 'PRESTAMOS',
  6: 'GASTOS X COMPROBAR',
};

/** @param {Array<{ codigo?: number|string, nombre?: string }>|null|undefined} catalog */
export function buildTipoGastosLookup(catalog) {
  const map = new Map(
    Object.entries(TIPO_GASTOS_LEGACY).map(([k, v]) => [Number(k), v]),
  );
  for (const item of catalog || []) {
    const codigo = Math.trunc(Number(item?.codigo));
    const nombre = String(item?.nombre ?? '').trim();
    if (Number.isFinite(codigo) && nombre) map.set(codigo, nombre);
  }
  return map;
}

function resolveTipoNombreFromLookup(tipo, tipoNombre, lookup) {
  const raw = String(tipoNombre ?? '').trim();
  if (raw && raw !== '???') return raw;
  if (tipo != null && lookup?.get?.(tipo)) return lookup.get(tipo);
  if (tipo != null && TIPO_GASTOS_LEGACY[tipo]) return TIPO_GASTOS_LEGACY[tipo];
  return raw || '???';
}

/** Resuelve la línea de encabezado «código nombre» del filtro tipo de gasto. */
export function resolveGastosTipoGastoTexto(tipoGasto, apiTexto, catalog) {
  const api = String(apiTexto ?? '').trim();
  if (api && !api.includes('???')) return api;
  const codigo = Math.trunc(Number(tipoGasto));
  if (!Number.isFinite(codigo)) return api;
  const lookup = buildTipoGastosLookup(catalog);
  const nombre = lookup.get(codigo) ?? TIPO_GASTOS_LEGACY[codigo];
  return nombre ? `${codigo} ${nombre}` : api || String(codigo);
}

export const GASTOS_DISPLAY_COLUMNS = [
  { id: 'fecha', label: 'Fecha', align: 'left', kind: 'fecha' },
  { id: 'folio', label: 'Folio', align: 'right', kind: 'text' },
  { id: 'concepto', label: 'Concepto', align: 'left', kind: 'text' },
  { id: 'importe', label: 'Importe', align: 'right', total: true, kind: 'money' },
];

/** Vista global legacy (rptGastosGlobal): solo tipo de gasto y monto total. */
export const GASTOS_GLOBAL_COLUMNS = [
  { id: 'tipoLabel', label: 'Tipo de gasto', align: 'left', kind: 'text' },
  { id: 'importe', label: 'Importe', align: 'right', total: true, kind: 'money' },
];

const ORDINAL_TURNO = ['', 'Primer', 'Segundo', 'Tercer', 'Cuarto', 'Quinto'];

function fmtMoney(n) {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const nFinite = (v) => {
  if (v == null || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/** @param {Record<string, unknown>|null|undefined} raw @param {Map<number, string>|null|undefined} tipoLookup */
export function rowToGastosNorm(raw, tipoLookup = null) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const importeRaw = r.importe ?? (nFinite(r.cantidad) != null && nFinite(r.cambio) != null
    ? Number(r.cantidad) - Number(r.cambio)
    : null);
  let tipo = r.tipo;
  if (tipo != null && tipo !== '') {
    const t = Math.trunc(Number(tipo));
    tipo = Number.isFinite(t) ? t : null;
  } else {
    tipo = null;
  }
  let folio = r.folio;
  if (folio != null && folio !== '') {
    const f = Math.trunc(Number(folio));
    folio = Number.isFinite(f) ? f : folio;
  } else {
    folio = null;
  }
  const tipoNombre = resolveTipoNombreFromLookup(
    tipo,
    r.tipoNombre ?? r.tipo_nombre ?? r.tipoNombreGasto ?? r['Tipo nombre'],
    tipoLookup,
  );
  return {
    fecha: r.fecha,
    folio,
    concepto: String(r.concepto ?? '').trim() || '—',
    importe: nFinite(importeRaw) ?? 0,
    tipo,
    tipoNombre,
  };
}

/** @param {ReturnType<typeof rowToGastosNorm>[]} normRows */
export function groupGastosByTipo(normRows) {
  /** @type {Array<{ tipo: number|null, tipoNombre: string, label: string, rows: ReturnType<typeof rowToGastosNorm>[], subtotal: number }>} */
  const groups = [];
  let current = null;

  for (const row of normRows) {
    const tipoKey = row.tipo ?? null;
    if (!current || current.tipo !== tipoKey) {
      const tipoNombre = String(row.tipoNombre || '???').trim() || '???';
      const code = tipoKey != null ? String(tipoKey) : '?';
      current = {
        tipo: tipoKey,
        tipoNombre,
        label: `${code} ${tipoNombre.toUpperCase()}`,
        rows: [],
        subtotal: 0,
      };
      groups.push(current);
    }
    current.rows.push(row);
    current.subtotal = Math.round((current.subtotal + (Number(row.importe) || 0)) * 100) / 100;
  }
  return groups;
}

/** @param {ReturnType<typeof rowToGastosNorm>[]} normRows */
export function sumGastosImporteTotal(normRows) {
  let s = 0;
  for (const r of normRows) {
    const n = Number(r.importe);
    if (!Number.isNaN(n)) s += n;
  }
  return Math.round(s * 100) / 100;
}

/** @param {Record<string, unknown>|null|undefined} apiTotals @param {number} fallback */
export function resolveGastosGrandTotal(apiTotals, fallback) {
  const fromApi = nFinite(apiTotals?.importe ?? apiTotals?.totalImporte ?? apiTotals?.total);
  return fromApi != null ? fromApi : fallback;
}

/** @param {Record<string, string>|null|undefined} columnas @param {boolean} [globalView] */
export function applyGastosColumnLabels(columnas, globalView = false) {
  if (globalView) {
    return GASTOS_GLOBAL_COLUMNS.map((c) => ({ ...c }));
  }
  if (!columnas || typeof columnas !== 'object' || Array.isArray(columnas)) {
    return GASTOS_DISPLAY_COLUMNS.map((c) => ({ ...c }));
  }
  return GASTOS_DISPLAY_COLUMNS.map((c) => {
    const label = columnas[c.id];
    if (typeof label === 'string' && label.trim()) return { ...c, label: label.trim() };
    return { ...c };
  });
}

/** Filas resumidas por tipo para vista global (sin detalle de conceptos). */
export function buildGastosTipoSummaryRows(groups = []) {
  return groups.map((g) => ({
    tipoLabel: g.label,
    importe: g.subtotal,
  }));
}

export function formatGastosFechaDisplay(value) {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}:\d{2}))?/);
  if (m) {
    const date = `${m[3]}/${m[2]}/${m[1]}`;
    return m[4] ? `${date} ${m[4]}` : date;
  }
  const dm = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}:\d{2}:\d{2}))?/);
  if (dm) return dm[4] ? `${dm[1]}/${dm[2]}/${dm[3]} ${dm[4]}` : `${dm[1]}/${dm[2]}/${dm[3]}`;
  return s;
}

export function formatGastosCell(spec, value) {
  if (value == null || value === '') return '—';
  if (spec.kind === 'fecha') return formatGastosFechaDisplay(value);
  if (spec.kind === 'money') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : fmtMoney(n);
  }
  return String(value);
}

export function formatGastosPeriodoLegacy(fechaInicio, fechaFin) {
  const fmt = (iso) => (iso ? isoADisplayDDMMAAAA(String(iso).split('T')[0]) : '—');
  return `DEL ${fmt(fechaInicio)} AL ${fmt(fechaFin)}`;
}

function formatGastosTurnoLegacyLabel(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n ?? '—');
  if (num >= 1 && num < ORDINAL_TURNO.length) return `${num}:${ORDINAL_TURNO[num]} Turno`;
  return `${num}:Turno`;
}

export function getTurnoNombreDisplay(codigo) {
  const num = Number(codigo);
  if (!Number.isFinite(num) || num < 1) return '';
  if (num >= 1 && num < ORDINAL_TURNO.length) return `${ORDINAL_TURNO[num]} Turno`;
  return `Turno ${num}`;
}

/** Línea tipo «1 CAJA GENERAL.. Turnos 1:Primer Turno AL 1:Primer Turno». */
export function formatGastosCajaTurnosLegacyLine(enc, form = {}) {
  const e = enc && typeof enc === 'object' ? enc : {};
  const f = form && typeof form === 'object' ? form : {};

  let cajaPart = '';
  const cajItems = Array.isArray(e.cajasItems)
    ? e.cajasItems.map((x) => String(x ?? '').trim()).filter(Boolean)
    : [];
  const cajasTexto = String(e.cajasTexto || '').trim();
  if (cajItems.length > 0) {
    cajaPart = cajItems.length <= 2 ? cajItems.join(' · ') : cajItems.join(', ');
  } else if (cajasTexto && cajasTexto !== 'Todas las cajas') {
    const same = cajasTexto.match(/^(\d+\s+.+?)\s+a\s+\1$/i);
    if (same) cajaPart = same[1].trim();
    else {
      const first = cajasTexto.match(/^(\d+\s+.+?)\s+a\s+/i);
      cajaPart = first ? first[1].trim() : cajasTexto;
    }
  } else if (f.cajaInicio != null || f.cajaFin != null) {
    cajaPart = String(f.cajaInicio ?? f.cajaFin ?? '');
  }

  let turnosPart = '';
  const turnosTexto = String(e.turnosTexto || '').trim();
  if (f.turnoInicio != null || f.turnoFin != null) {
    const ti = f.turnoInicio ?? f.turnoFin;
    const tf = f.turnoFin ?? f.turnoInicio;
    turnosPart = `Turnos ${formatGastosTurnoLegacyLabel(ti)} AL ${formatGastosTurnoLegacyLabel(tf)}`;
  } else if (turnosTexto && turnosTexto !== 'Todos los turnos') {
    const m = turnosTexto.match(/^(\d+)\s+(\S+)\s+Turno\s+a\s+(\d+)\s+(\S+)\s+Turno$/i);
    if (m) {
      turnosPart = `Turnos ${m[1]}:${m[2]} Turno AL ${m[3]}:${m[4]} Turno`;
    } else {
      turnosPart = `Turnos ${turnosTexto}`;
    }
  }

  if (cajaPart && turnosPart) return `${cajaPart}.. ${turnosPart}`;
  return [cajaPart, turnosPart].filter(Boolean).join(' ');
}

export function mergeGastosEncabezado(apiEnc, encabezadoFallback) {
  return mergePopularidadEncabezado(apiEnc, encabezadoFallback, {
    reporteTitulo: GASTOS_REPORT_TITULO_DEFAULT,
    incluirCortesias: false,
    lineaCortesias: '',
    cortesiasSubtitle: '',
  });
}

export function withGastosPeriodoLine(enc, fechaInicio, fechaFin, form = {}) {
  const e = enc && typeof enc === 'object' ? { ...enc } : {};
  e.periodoTexto = formatGastosPeriodoLegacy(fechaInicio, fechaFin);
  e.cajaTurnosTexto = formatGastosCajaTurnosLegacyLine(e, form);
  e.reporteTitulo = e.reporteTitulo || GASTOS_REPORT_TITULO_DEFAULT;
  return e;
}
